import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

/**
 * Response interceptor — gestion propre des 401 (session expirée).
 *
 * Objectif : éviter que l'iframe (WordPress / preview Emergent) plante quand un
 * token expire après une longue inactivité.
 * - On PURGE le token localement immédiatement.
 * - On NE FAIT PAS de redirect hard (window.location) qui pourrait casser l'iframe.
 * - On laisse l'erreur remonter aux appelants (toast + ProtectedRoute route vers /login
 *   en client-side, ce qui reste dans l'iframe).
 * - Les endpoints publics (catalog, quotes/preview, color-request, POST quotes anonyme)
 *   ne renvoient jamais 401, donc /embed n'est jamais impacté.
 */
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      try {
        // Signal cross-composants : les providers/routes protégées peuvent réagir
        // sans qu'on ait besoin de recharger l'iframe.
        window.dispatchEvent(new Event("aih:session-expired"));
      } catch (_) { /* ignore */ }
    }
    return Promise.reject(error);
  },
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous, obj = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem("aih_token");
    refresh();
  }, [refresh]);

  // Écoute des expirations 401 déclenchées par l'intercepteur axios.
  // Permet de basculer l'état user sans reload de page (safe pour iframe).
  useEffect(() => {
    const onExpire = () => setUser(null);
    window.addEventListener("aih:session-expired", onExpire);
    return () => window.removeEventListener("aih:session-expired", onExpire);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data; // returns user in pending status (no token)
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* cookie is cleared server-side when reachable */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside provider");
  return ctx;
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return err?.message || "Une erreur est survenue";
}
